<template>
  <div class="card-preview">
    <div class="card-preview-stage" :class="{ landscape: isLandscape }">
      <img
        v-if="svgUri"
        :src="svgUri"
        class="card-preview-img"
        :alt="`${card.name || 'Card'} preview`"
      />
      <div v-else-if="loading" class="card-preview-empty">
        <div class="spinner-border spinner-border-sm text-secondary"></div>
      </div>
      <div v-else class="card-preview-empty">
        <div class="text-muted small text-center px-3">
          {{ error || 'Start typing and your card appears here.' }}
        </div>
      </div>
    </div>

    <!-- The fit report. This is the whole point: it says "this will be small
         on paper" while there is still time to shorten the text. -->
    <div v-if="fit" class="fit-bar" :class="fitLevel">
      <span class="fit-dot"></span>
      <span class="fit-text">
        <template v-if="fitLevel === 'over'">
          Too long — trimmed to fit. Shorten the rules text.
        </template>
        <template v-else-if="fitLevel === 'warn'">
          Shrunk to {{ fit.fontSize }}pt to fit — consider shortening.
        </template>
        <template v-else>
          {{ fit.fontSize }}pt · {{ fit.lines }} {{ fit.lines === 1 ? 'line' : 'lines' }} · fits
        </template>
      </span>
      <span class="fit-count">{{ fit.characters }} chars</span>
    </div>
  </div>
</template>

<script>
import { sparkforgeApi, svgToDataUri } from '@/sparkforgeApi';

export default {
  name: 'CardPreview',
  props: {
    gameId: { type: String, required: true },
    card: { type: Object, required: true },
    // Try a layout or style without saving it.
    builtin: { type: String, default: null },
    styleOverride: { type: Object, default: null },
    debounce: { type: Number, default: 300 }
  },
  data() {
    return {
      svgUri: null,
      fit: null,
      template: null,
      loading: false,
      error: null,
      isLandscape: false,
      timer: null,
      controller: null
    };
  },
  computed: {
    fitLevel() {
      if (!this.fit) return 'ok';
      if (this.fit.overflowed) return 'over';
      return this.fit.shrunk ? 'warn' : 'ok';
    },
    // Only these fields change the rendering, so only these should refetch.
    watchKey() {
      const c = this.card || {};
      return JSON.stringify([
        c.name, c.description, c.type, c.subtype, c.flavorText, c.repeats,
        c.image_url, this.builtin, this.styleOverride
      ]);
    }
  },
  watch: {
    watchKey: {
      immediate: true,
      handler() {
        this.schedule();
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
  },
  methods: {
    schedule() {
      clearTimeout(this.timer);
      const empty = !this.card || (!this.card.name && !this.card.description);
      if (empty) {
        this.svgUri = null;
        this.fit = null;
        return;
      }
      // Debounced: this fires on every keystroke otherwise.
      this.timer = setTimeout(this.render, this.debounce);
    },
    async render() {
      if (!this.gameId) return;
      // Cancel the in-flight request so a slow one cannot overwrite a newer
      // result and show stale art.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.loading = true;
      this.error = null;
      try {
        const result = await sparkforgeApi.previewCard(this.gameId, this.card, {
          builtin: this.builtin,
          style: this.styleOverride,
          signal: this.controller.signal
        });
        this.svgUri = svgToDataUri(result.svg);
        this.fit = result.fit;
        this.template = result.template;
        this.isLandscape = /width="(\d+)"[\s\S]*?height="(\d+)"/.test(result.svg)
          ? Number(RegExp.$1) > Number(RegExp.$2)
          : false;
        this.$emit('rendered', result);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.error = err.message;
        this.$emit('error', err);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.card-preview {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: center;
}

/* Fixed aspect box so the card does not jump around while typing. */
.card-preview-stage {
  width: 100%;
  max-width: 280px;
  aspect-ratio: 180 / 252;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background:
    linear-gradient(45deg, rgba(0, 0, 0, 0.025) 25%, transparent 25%) -8px 0 / 16px 16px,
    linear-gradient(-45deg, rgba(0, 0, 0, 0.025) 25%, transparent 25%) -8px 0 / 16px 16px,
    var(--bg-surface, #fff);
}

.card-preview-stage.landscape {
  aspect-ratio: 252 / 180;
  max-width: 380px;
}

.card-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.16));
  border-radius: 10px;
}

.card-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: 1px dashed var(--border-sketch, #d5cfc4);
  border-radius: 12px;
}

.fit-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 300px;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  font-size: 0.78rem;
  border: 1px solid transparent;
}

.fit-bar.ok    { background: #eef7ef; border-color: #cfe6d3; color: #2f6b3a; }
.fit-bar.warn  { background: #fdf5e6; border-color: #f0dfb8; color: #8a6220; }
.fit-bar.over  { background: #fdeceb; border-color: #f2c9c6; color: #9d332c; }

.fit-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 auto;
}

.fit-text { flex: 1; }
.fit-count { opacity: 0.65; font-variant-numeric: tabular-nums; }
</style>
